import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { GlassCard } from '@/components/brand/GlassCard';
import { GlowButton } from '@/components/brand/GlowButton';
import { AdminFormField } from '@/components/forms/FormField';
import { adminListPosts, adminCreatePost, adminUpdatePost, adminDeletePost } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit3 } from 'lucide-react';
import { formatDate } from '@/lib/utils';

const EMPTY = { title: '', excerpt: '', content: '', category: 'Advisory', tags: '', reading_minutes: 5, cover_image: '', published: true };

// --- Helpers ----------------------------------------------------------------

const postToFormState = (post) => ({
    title: post.title || '',
    excerpt: post.excerpt || '',
    content: post.content || '',
    category: post.category || 'Advisory',
    tags: (post.tags || []).join(', '),
    reading_minutes: post.reading_minutes || 5,
    cover_image: post.cover_image || '',
    published: !!post.published,
});

const formToPostPayload = (form) => ({
    title: form.title,
    excerpt: form.excerpt,
    content: form.content,
    category: form.category,
    tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
    reading_minutes: parseInt(form.reading_minutes || 5, 10) || 5,
    cover_image: form.cover_image,
    published: !!form.published,
});

// --- Sub-components ---------------------------------------------------------

const POSTS_TABLE_HEADERS = ['Title', 'Category', 'Reading', 'Status', 'Updated', 'Actions'];

const StatusBadge = ({ published }) => (
    <span className={`text-[10px] tracking-[0.14em] uppercase px-2 py-1 rounded-la-sm border ${published ? 'border-emerald-400/30 text-emerald-200 bg-emerald-200/10' : 'border-white/10 text-la-text3'}`}>
        {published ? 'Published' : 'Draft'}
    </span>
);

const PostRow = ({ post, onEdit, onDelete }) => (
    <tr className="border-t border-white/10 hover:bg-white/[0.03]">
        <td className="px-4 py-3 text-la-text">{post.title}</td>
        <td className="px-4 py-3 text-la-text2">{post.category}</td>
        <td className="px-4 py-3 text-la-text2">{post.reading_minutes} min</td>
        <td className="px-4 py-3"><StatusBadge published={post.published} /></td>
        <td className="px-4 py-3 text-la-text3">{formatDate(post.updated_at)}</td>
        <td className="px-4 py-3">
            <div className="inline-flex items-center gap-3">
                <button onClick={() => onEdit(post)} className="inline-flex items-center gap-1 text-la-text2 hover:text-la-text" data-testid="admin-edit-post"><Edit3 className="h-3.5 w-3.5" /> Edit</button>
                <button onClick={() => onDelete(post)} className="inline-flex items-center gap-1 text-la-text2 hover:text-rose-300" data-testid="admin-delete-post"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
            </div>
        </td>
    </tr>
);

const PostsTable = ({ posts, onEdit, onDelete }) => (
    <GlassCard className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
            <table data-testid="admin-table" className="w-full text-sm">
                <thead className="bg-white/[0.04]">
                    <tr className="text-[11px] tracking-[0.14em] uppercase text-la-text3">
                        {POSTS_TABLE_HEADERS.map((h) => (
                            <th key={h} className="text-left px-4 py-3">{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {posts.length === 0 ? (
                        <tr><td colSpan={POSTS_TABLE_HEADERS.length} className="px-4 py-10 text-center text-la-text3">No posts yet.</td></tr>
                    ) : posts.map((p) => <PostRow key={p.post_id} post={p} onEdit={onEdit} onDelete={onDelete} />)}
                </tbody>
            </table>
        </div>
    </GlassCard>
);

const PostFormDialog = ({ open, isNew, form, onChange, onPublishedToggle, onClose, onSubmit, submitting }) => (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogTrigger asChild><span /></DialogTrigger>
        <DialogContent className="bg-la-panel border border-white/10 text-la-text max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
                <DialogTitle className="font-display text-2xl">{isNew ? 'New article' : 'Edit article'}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <AdminFormField label="Title" value={form.title} onChange={onChange('title')} testId="admin-post-title" />
                <AdminFormField label="Category" value={form.category} onChange={onChange('category')} testId="admin-post-category" />
                <AdminFormField label="Cover image URL" value={form.cover_image} onChange={onChange('cover_image')} testId="admin-post-image" />
                <AdminFormField label="Reading minutes" value={form.reading_minutes} onChange={onChange('reading_minutes')} type="number" testId="admin-post-reading" />
                <AdminFormField label="Tags (comma separated)" value={form.tags} onChange={onChange('tags')} testId="admin-post-tags" />
                <div className="flex items-end">
                    <label className="inline-flex items-center gap-2 text-sm text-la-text2">
                        <input type="checkbox" checked={!!form.published} onChange={onPublishedToggle} /> Published
                    </label>
                </div>
            </div>
            <div className="mt-3">
                <label className="meta-eyebrow">Excerpt</label>
                <textarea value={form.excerpt} onChange={onChange('excerpt')} rows={2} className="mt-2 w-full rounded-la-sm bg-white/[0.04] border border-white/10 px-3 py-2 text-sm" data-testid="admin-post-excerpt" />
            </div>
            <div className="mt-3">
                <label className="meta-eyebrow">Content (supports paragraphs and `### Heading` syntax)</label>
                <textarea value={form.content} onChange={onChange('content')} rows={12} className="mt-2 w-full rounded-la-sm bg-white/[0.04] border border-white/10 px-3 py-2 text-sm font-mono" data-testid="admin-post-content" />
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
                <button onClick={onClose} className="text-sm text-la-text2 hover:text-la-text px-3 py-2">Cancel</button>
                <GlowButton onClick={onSubmit} disabled={submitting} data-testid="admin-save-post-button">{submitting ? 'Saving…' : 'Save'}</GlowButton>
            </div>
        </DialogContent>
    </Dialog>
);

// --- Main page --------------------------------------------------------------

export default function AdminPostsPage() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // post object | 'new' | null
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        try { setItems(await adminListPosts()); }
        catch { toast.error('Could not load posts.'); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openNew = useCallback(() => { setEditing('new'); setForm(EMPTY); }, []);
    const openEdit = useCallback((p) => { setEditing(p); setForm(postToFormState(p)); }, []);
    const close = useCallback(() => { setEditing(null); setForm(EMPTY); }, []);

    const set = useCallback((k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value })), []);
    const togglePublished = useCallback((e) => setForm((f) => ({ ...f, published: e.target.checked })), []);

    const submit = useCallback(async () => {
        if (!form.title || !form.excerpt || !form.content) {
            toast.error('Title, excerpt and content required.');
            return;
        }
        const payload = formToPostPayload(form);
        try {
            setSubmitting(true);
            if (editing === 'new') {
                await adminCreatePost(payload);
                toast.success('Post created.');
            } else {
                await adminUpdatePost(editing.post_id, payload);
                toast.success('Post updated.');
            }
            close();
            load();
        } catch (err) {
            toast.error('Could not save post.');
        } finally {
            setSubmitting(false);
        }
    }, [editing, form, close, load]);

    const del = useCallback(async (p) => {
        if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
        await adminDeletePost(p.post_id);
        toast.success('Post deleted.');
        load();
    }, [load]);

    return (
        <AdminLayout title="Insights CMS">
            <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-la-text2">Manage editorial articles published on the Insights section.</p>
                <GlowButton onClick={openNew} data-testid="admin-new-post-button">
                    <Plus className="h-4 w-4" /> New article
                </GlowButton>
            </div>
            <PostsTable posts={items} onEdit={openEdit} onDelete={del} />
            <PostFormDialog
                open={!!editing}
                isNew={editing === 'new'}
                form={form}
                onChange={set}
                onPublishedToggle={togglePublished}
                onClose={close}
                onSubmit={submit}
                submitting={submitting}
            />
        </AdminLayout>
    );
}
